/**
 * Máquina de estados PURA del reproductor de la batería PEFF (fase 4).
 * Sin React, sin red, sin efectos: (estado, acción) → estado. Determinística y
 * testeada en aislamiento (test/peff-player.test.ts). Los efectos (audio modelo,
 * MediaRecorder, /v1/media, /v1/assessment) viven en usePeffPlayer.ts; la UI
 * (PeffPlayer.tsx) solo pinta el estado y despacha intenciones.
 *
 * Dos niveles, como el modelo del dominio (batería → ejercicio → ítem):
 *
 *  1. Sesión de administración:
 *     mic_check → consigna_ejercicio → ejercicio → pausa → … →
 *       (banda A, 1.ª sesión) sesion_cerrada
 *       (última sesión)        listo_para_procesar → procesando → hecho
 *     En cualquier punto: suspendida (botón del adulto o racha sin intento).
 *
 *  2. Ítem: `paso` (guion clínico de §2.1/§3.1) × `rec` (captura de audio).
 *     F1/F3/F4  modelo → respuesta
 *     PSF2      modelo → distractora → consigna → respuesta
 *     F2/PSF1   pregunta →(5 s)→ pista → pregunta →(5 s)→ no_evoca
 *
 * Reglas clínicas que esta máquina garantiza:
 *  - Nunca se califica bien/mal: la estrella se gana por completar la captura.
 *  - Denominación: máximo UNA ayuda por ítem (`withHint`), luego `NO_EVOCA`.
 *  - Un ejercicio iniciado se completa o se descarta; la reanudación lo reinicia.
 */
import type { CodigoEjercicio, MotivoSuspension } from "../api/types.js";
import { MECANICA, UMBRAL_SIN_INTENTO } from "./mecanica.js";

// ---- modelo ---------------------------------------------------------------

/** Paso del guion del ítem (qué toca hacer, según §2.1/§3.1). */
export type Paso =
  | "modelo"       // suena el audio modelo (F1, F3, F4, PSF2)
  | "pregunta"     // "¿Qué es?" con el micrófono abierto (F2, PSF1)
  | "pista"        // suena la pista semántica (withHint queda en true)
  | "distractora"  // pregunta breve intercalada (PSF2)
  | "consigna"     // "Ahora dila tú" (PSF2)
  | "respuesta"    // el niño responde; se graba
  | "no_evoca";    // dos ventanas sin respuesta: se registra y se avanza

/** Sub-fase de captura+subida del audio del ítem. */
export type Rec = "idle" | "grabando" | "grabado" | "subiendo" | "guardado" | "error";

export type EstadoSesion =
  | "mic_check"
  | "consigna_ejercicio"
  | "ejercicio"
  | "pausa"
  | "sesion_cerrada"       // banda A: "continuaremos otro día"
  | "listo_para_procesar"
  | "procesando"
  | "hecho"
  | "suspendida";

export interface ItemRun {
  id: string;
  /** Sílaba/palabra/oración. En F4 NUNCA se muestra al niño. */
  ref: string;
  wordId: string | null;
  phoneme: string;
  /** Pista semántica pregrabada (word-bank); null → placeholder TTS marcado. */
  pistaSemantica: string | null;
  /** Pregunta distractora breve (word-bank); null → placeholder TTS marcado. */
  preguntaDistractora: string | null;
  /** URL de la imagen de estímulo (denominación); null → fallback a texto. */
  imagenUrl: string | null;
  /** URL del audio modelo humano; null → sin audio (no hay TTS de relleno). */
  audioModeloUrl: string | null;
}

export interface EjercicioRun {
  id: string;
  codigo: CodigoEjercicio;
  items: ItemRun[];
}

/** Flags de administración del ítem actual (se envían en PATCH /items/:id). */
export interface FlagsItem {
  withHint: boolean;
  noEvoca: boolean;
  sinIntento: boolean;
  revisarAudio: boolean;
  dudoso: boolean;
}

export interface EstadoPlayer {
  ejercicios: EjercicioRun[];
  ejercicioIdx: number;
  itemIdx: number;
  paso: Paso;
  rec: Rec;
  flags: FlagsItem;
  /** Ítems consecutivos sin intento (regla de suspensión §1.4). */
  rachaSinIntento: number;
  estrellas: number;
  estado: EstadoSesion;
  /** Quedan ejercicios de otra sesión de administración (banda A). */
  haySesionPendiente: boolean;
  motivoSuspension: MotivoSuspension | null;
  error: string | null;
}

export type AccionPlayer =
  | { tipo: "MIC_OK" }
  /** `items` refresca los del ejercicio: los adaptativos (F3/PSF2) solo se
   *  materializan en el API al iniciarlos, así que llegan aquí. */
  | { tipo: "INICIAR_EJERCICIO"; items?: ItemRun[] }
  /** El POST /start falló en la intro (fuera del flujo de grabación): el error
   *  se muestra aquí, no con SUBIR_ERROR (que solo aplica durante la subida). */
  | { tipo: "INICIAR_EJERCICIO_ERROR"; mensaje: string }
  | { tipo: "MODELO_FIN" }
  | { tipo: "ESPERA_AGOTADA" }
  | { tipo: "PISTA_FIN" }
  | { tipo: "DISTRACTORA_OK" }
  | { tipo: "CONSIGNA_FIN" }
  | { tipo: "GRABAR_INICIO" }
  | { tipo: "GRABAR_FIN" }
  | { tipo: "SUBIR_INICIO" }
  | { tipo: "SUBIR_OK" }
  | { tipo: "SUBIR_ERROR"; mensaje: string }
  | { tipo: "RESET_ERROR" }
  | { tipo: "REGRABAR" }
  | { tipo: "MARCAR_DUDOSO" }
  | { tipo: "MARCAR_SIN_INTENTO" }
  | { tipo: "SIGUIENTE_ITEM" }
  | { tipo: "SIGUIENTE_EJERCICIO" }
  | { tipo: "SUSPENDER"; motivo: MotivoSuspension }
  /** Reanudación: el API descartó el intento del ejercicio suspendido y el
   *  cliente reconstruye el plan desde la batería recargada. */
  | { tipo: "REANUDAR"; plan: InitPlayerInput }
  | { tipo: "PROCESAR" }
  | { tipo: "COMPLETADO" };

const SIN_FLAGS: FlagsItem = {
  withHint: false,
  noEvoca: false,
  sinIntento: false,
  revisarAudio: false,
  dudoso: false,
};

/** Primer paso del guion del ítem según la mecánica del ejercicio. */
export function pasoInicial(codigo: CodigoEjercicio): Paso {
  const m = MECANICA[codigo];
  if (m.modeloAuto) return "modelo";
  if (m.denominacion) return "pregunta";
  return "respuesta";
}

export interface InitPlayerInput {
  /** Ejercicios pendientes de la sesión de administración en curso, en orden. */
  ejercicios: EjercicioRun[];
  /** true si la batería tiene ejercicios de una sesión posterior (banda A). */
  haySesionPendiente: boolean;
  /** La prueba de micrófono ya quedó registrada (reanudación). */
  micCheckHecho: boolean;
}

export function initPlayer(input: InitPlayerInput): EstadoPlayer {
  return {
    ejercicios: input.ejercicios,
    ejercicioIdx: 0,
    itemIdx: 0,
    paso: "respuesta",
    rec: "idle",
    flags: { ...SIN_FLAGS },
    rachaSinIntento: 0,
    estrellas: 0,
    estado: input.micCheckHecho ? "consigna_ejercicio" : "mic_check",
    haySesionPendiente: input.haySesionPendiente,
    motivoSuspension: null,
    error: null,
  };
}

// ---- selectores puros -----------------------------------------------------

export function ejercicioActual(s: EstadoPlayer): EjercicioRun | null {
  return s.ejercicios[s.ejercicioIdx] ?? null;
}

export function itemActual(s: EstadoPlayer): ItemRun | null {
  return ejercicioActual(s)?.items[s.itemIdx] ?? null;
}

export function mecanicaActual(s: EstadoPlayer) {
  const e = ejercicioActual(s);
  return e ? MECANICA[e.codigo] : null;
}

export function esUltimoItem(s: EstadoPlayer): boolean {
  const e = ejercicioActual(s);
  return !!e && s.itemIdx >= e.items.length - 1;
}

export function esUltimoEjercicio(s: EstadoPlayer): boolean {
  return s.ejercicioIdx >= s.ejercicios.length - 1;
}

export function progreso(s: EstadoPlayer): { n: number; total: number; pct: number } {
  const total = ejercicioActual(s)?.items.length ?? 0;
  const n = Math.min(s.itemIdx + 1, total);
  return { n, total, pct: total === 0 ? 0 : Math.round((n / total) * 100) };
}

/** Se avanza con el audio guardado o con el ítem cerrado como NO_EVOCA. */
export function puedeAvanzar(s: EstadoPlayer): boolean {
  if (s.estado !== "ejercicio") return false;
  return s.rec === "guardado" || s.paso === "no_evoca" || s.flags.sinIntento;
}

/** ¿Corre la ventana de 5 s de la denominación? (la UI arma el temporizador). */
export function esperaDenominacionActiva(s: EstadoPlayer): boolean {
  return s.estado === "ejercicio" && s.paso === "pregunta" && s.rec !== "guardado";
}

/** Negativa sostenida: >3 ítems consecutivos sin intento (§1 regla 4). */
export function debeSuspenderAuto(s: EstadoPlayer): boolean {
  return s.rachaSinIntento > UMBRAL_SIN_INTENTO;
}

// ---- reducer --------------------------------------------------------------

/** Arranca el ítem `idx` del ejercicio actual: guion al inicio, flags limpias. */
function entrarAItem(s: EstadoPlayer, idx: number): EstadoPlayer {
  const e = ejercicioActual(s);
  return {
    ...s,
    itemIdx: idx,
    paso: e ? pasoInicial(e.codigo) : "respuesta",
    rec: "idle",
    flags: { ...SIN_FLAGS },
    error: null,
  };
}

/** Cierre del ejercicio actual: pausa, cierre de sesión o listo para procesar. */
function cerrarEjercicio(s: EstadoPlayer): EstadoPlayer {
  if (!esUltimoEjercicio(s)) return { ...s, estado: "pausa", rec: "idle" };
  return {
    ...s,
    rec: "idle",
    estado: s.haySesionPendiente ? "sesion_cerrada" : "listo_para_procesar",
  };
}

export function playerReducer(s: EstadoPlayer, a: AccionPlayer): EstadoPlayer {
  switch (a.tipo) {
    case "MIC_OK":
      if (s.estado !== "mic_check") return s;
      return { ...s, estado: "consigna_ejercicio" };

    case "INICIAR_EJERCICIO": {
      if (s.estado !== "consigna_ejercicio") return s;
      const actual = ejercicioActual(s);
      if (!actual) return s;
      const e = a.items ? { ...actual, items: a.items } : actual;
      const ejercicios = s.ejercicios.map((x, i) => (i === s.ejercicioIdx ? e : x));
      const base = { ...s, ejercicios, estado: "ejercicio" as const };
      // Un adaptativo sin ítems elegibles (F3/PSF2) no se administra: el API lo
      // descarta y aquí se cierra sin pintar ningún ítem.
      if (e.items.length === 0) return cerrarEjercicio(base);
      return entrarAItem(base, 0);
    }

    case "INICIAR_EJERCICIO_ERROR":
      // Se queda en la intro y muestra el error; el adulto puede reintentar.
      if (s.estado !== "consigna_ejercicio") return s;
      return { ...s, error: a.mensaje };

    case "MODELO_FIN": {
      if (s.estado !== "ejercicio" || s.paso !== "modelo") return s;
      const m = mecanicaActual(s);
      return { ...s, paso: m?.distractora ? "distractora" : "respuesta" };
    }

    case "DISTRACTORA_OK":
      // La respuesta a la distractora NO se graba como ítem: solo demora la
      // repetición para evitar la imitación ecoica (§3.1, PSF2).
      if (s.estado !== "ejercicio" || s.paso !== "distractora") return s;
      return { ...s, paso: "consigna" };

    case "CONSIGNA_FIN":
      if (s.estado !== "ejercicio" || s.paso !== "consigna") return s;
      return { ...s, paso: "respuesta" };

    case "ESPERA_AGOTADA": {
      // Solo en denominación y solo si el niño aún no respondió.
      if (!esperaDenominacionActiva(s)) return s;
      if (s.rec === "grabado" || s.rec === "subiendo") return s;
      if (!s.flags.withHint) {
        // Primera ventana: UNA sola ayuda por ítem (§3.1 regla del PPTX).
        return { ...s, paso: "pista", rec: "idle", flags: { ...s.flags, withHint: true } };
      }
      // Segunda ventana con pista ya dada: no evoca. Se descarta la captura vacía.
      return {
        ...s,
        paso: "no_evoca",
        rec: "idle",
        flags: { ...s.flags, noEvoca: true, sinIntento: true },
      };
    }

    case "PISTA_FIN":
      if (s.estado !== "ejercicio" || s.paso !== "pista") return s;
      return { ...s, paso: "pregunta" };

    case "GRABAR_INICIO":
      if (s.estado !== "ejercicio") return s;
      if (s.paso !== "pregunta" && s.paso !== "respuesta") return s;
      if (s.rec !== "idle") return s;
      return { ...s, rec: "grabando", error: null };

    case "GRABAR_FIN":
      if (s.rec !== "grabando") return s;
      // Si respondió durante la ventana de la denominación, el guion cierra ahí.
      return { ...s, rec: "grabado", paso: s.paso === "pregunta" ? "respuesta" : s.paso };

    case "SUBIR_INICIO":
      if (s.rec !== "grabado") return s;
      return { ...s, rec: "subiendo", error: null };

    case "SUBIR_OK":
      if (s.rec !== "subiendo") return s;
      // Estrella por completitud (nunca por acierto).
      return { ...s, rec: "guardado", estrellas: s.estrellas + 1 };

    case "SUBIR_ERROR":
      if (s.rec !== "subiendo") return s;
      return { ...s, rec: "error", error: a.mensaje };

    case "RESET_ERROR":
      if (s.rec !== "error") return s;
      return { ...s, rec: "grabado", error: null };

    case "REGRABAR":
      if (s.estado !== "ejercicio") return s;
      if (s.rec === "grabando" || s.rec === "subiendo") return s;
      // La estrella ya ganada se conserva; el paso vuelve a la respuesta.
      return { ...s, rec: "idle", paso: "respuesta", error: null };

    case "MARCAR_DUDOSO":
      // El adulto marca la producción como dudosa → el API la manda al adaptativo.
      if (s.estado !== "ejercicio") return s;
      return { ...s, flags: { ...s.flags, dudoso: true } };

    case "MARCAR_SIN_INTENTO":
      if (s.estado !== "ejercicio") return s;
      if (s.rec === "grabando" || s.rec === "subiendo") return s;
      return { ...s, rec: "idle", flags: { ...s.flags, sinIntento: true } };

    case "SIGUIENTE_ITEM": {
      if (!puedeAvanzar(s)) return s;
      const racha = s.flags.sinIntento ? s.rachaSinIntento + 1 : 0;
      const conRacha = { ...s, rachaSinIntento: racha };
      // Negativa sostenida: se guarda y se reprograma (no se sigue administrando).
      if (debeSuspenderAuto(conRacha)) {
        return { ...conRacha, estado: "suspendida", motivoSuspension: "SIN_INTENTO", rec: "idle" };
      }
      if (esUltimoItem(conRacha)) return cerrarEjercicio(conRacha);
      return entrarAItem(conRacha, conRacha.itemIdx + 1);
    }

    case "SIGUIENTE_EJERCICIO":
      if (s.estado !== "pausa") return s;
      return { ...s, ejercicioIdx: s.ejercicioIdx + 1, itemIdx: 0, estado: "consigna_ejercicio" };

    case "SUSPENDER":
      if (s.estado === "procesando" || s.estado === "hecho" || s.estado === "suspendida") return s;
      return { ...s, estado: "suspendida", motivoSuspension: a.motivo, rec: "idle" };

    case "REANUDAR": {
      // El ejercicio suspendido vuelve a `pendiente` y se administra entero de
      // nuevo (nunca se retoma a mitad, §1 regla 3). Las estrellas ya ganadas se
      // conservan: son refuerzo del niño, no puntaje.
      if (s.estado !== "suspendida") return s;
      const fresco = initPlayer(a.plan);
      return { ...fresco, estrellas: s.estrellas };
    }

    case "PROCESAR":
      if (s.estado !== "listo_para_procesar") return s;
      return { ...s, estado: "procesando" };

    case "COMPLETADO":
      if (s.estado !== "procesando") return s;
      return { ...s, estado: "hecho" };

    default:
      return s;
  }
}
